import React, { useEffect, useState } from 'react';
import { Notification, NotificationPriority } from '../../types/notifications';

interface NotificationToastProps {
  notification: Notification;
  onDismiss: () => void;
  onAction?: () => void;
  duration?: number; // ms, 0 = no auto-dismiss
}

const priorityStyles: Record<NotificationPriority, { border: string; icon: string; bg: string }> = {
  [NotificationPriority.Critical]: { 
    border: 'border-l-red-600', 
    icon: 'text-red-600', 
    bg: 'bg-red-50'
  },
  [NotificationPriority.High]: { 
    border: 'border-l-orange-500', 
    icon: 'text-orange-500', 
    bg: 'bg-orange-50'
  },
  [NotificationPriority.Medium]: { 
    border: 'border-l-blue-500', 
    icon: 'text-blue-500', 
    bg: 'bg-blue-50'
  },
  [NotificationPriority.Low]: { 
    border: 'border-l-gray-400', 
    icon: 'text-gray-400', 
    bg: 'bg-gray-50'
  },
};

export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onDismiss,
  onAction,
  duration = 5000,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const priority = notification.priority as NotificationPriority;
  const styles = priorityStyles[priority];
  const isCritical = priority === NotificationPriority.Critical;

  useEffect(() => {
    if (duration === 0 || isCritical) return; // Don't auto-dismiss critical notifications

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
        handleDismiss();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, isCritical]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(onDismiss, 300);
  };

  return (
    <div
      className={`w-96 bg-white rounded-lg shadow-xl border border-gray-200 border-l-4 ${styles.border} overflow-hidden transform transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}
      role="alert"
      aria-live={isCritical ? 'assertive' : 'polite'}
    >
      <div className={`p-4 ${styles.bg}`}>
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`flex-shrink-0 ${styles.icon}`}>
            {isCritical ? (
              <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">
                {notification.title}
              </p>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 -mr-1 -mt-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                aria-label="Dismiss notification"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
              {notification.body}
            </p>

            {/* Case reference */}
            {notification.caseNumber && (
              <p className="mt-1 text-xs text-gray-500">
                Case: {notification.caseNumber}
              </p>
            )}

            {/* Action button */}
            {(notification.actionUrl || onAction) && (
              <a
                href={notification.actionUrl}
                onClick={(e) => {
                  if (onAction) {
                    e.preventDefault();
                    onAction();
                  }
                  handleDismiss();
                }}
                className={`inline-flex items-center mt-2 text-sm font-medium ${
                  isCritical ? 'text-red-700 hover:text-red-800' : 'text-blue-600 hover:text-blue-800'
                } transition-colors`}
              >
                {notification.actionLabel || 'View Details'}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar for auto-dismiss */}
      {duration > 0 && !isCritical && (
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-blue-500 transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
